import i18n from "i18next"
import LanguageDetector from "i18next-browser-languagedetector"
import Backend from "i18next-http-backend"
import { initReactI18next } from "react-i18next"

import type { CustomerConfig } from "./types/customer-config"

import customerConfig from "./customer/config.json"
import {
  enabledLanguageCodes,
  fallbackLanguage,
  loadedLanguageCodes,
  resourcesFor,
  toOfferedLanguage,
} from "./lib/languages"
import { getInjectedAppName } from "./lib/runtime-branding"
import { translation as deTranslation } from "./locales/de"
import { translation as enTranslation } from "./locales/en"
import { translation as fiTranslation } from "./locales/fi"
import { translation as noTranslation } from "./locales/no"
import { translation as svTranslation } from "./locales/sv"

const enabledLanguages = enabledLanguageCodes()
const loadedLanguages = loadedLanguageCodes(enabledLanguages)

void i18n
  .use(Backend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    // The offered languages plus Swedish, which the tender context always
    // needs (lib/languages.ts). What the interface may show is limited by
    // convertDetectedLanguage below and by the switcher (#3226).
    fallbackLng: fallbackLanguage(enabledLanguages),
    supportedLngs: loadedLanguages,
    resources: resourcesFor(
      {
        en: { translation: enTranslation },
        sv: { translation: svTranslation },
        no: { translation: noTranslation },
        fi: { translation: fiTranslation },
        de: { translation: deTranslation },
      },
      loadedLanguages
    ),
    detection: {
      order: ["localStorage", "cookie", "navigator"],
      caches: ["localStorage", "cookie"],
      // A saved or detected language the deployment does not offer falls back.
      convertDetectedLanguage: (detected: string) => toOfferedLanguage(detected, enabledLanguages),
    },
    keySeparator: ".", // Allows for nested translations
    interpolation: {
      escapeValue: false,
      defaultVariables: {
        appName:
          getInjectedAppName() ?? (customerConfig as CustomerConfig).config.app_name ?? "Hello AI",
      },
    },
    debug: false,
  })
  .then(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = i18n.resolvedLanguage ?? i18n.language
    }
  })

i18n.on("languageChanged", (lng) => {
  if (typeof document !== "undefined") {
    document.documentElement.lang = lng
  }
})

// eslint-disable-next-line import/no-default-export
export default i18n
